import {
	BodyScrollEndEvent,
	CellClickedEvent,
	FirstDataRenderedEvent,
	ICellRendererParams,
	ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { MouseEvent, useCallback, useMemo, useRef } from "react";
import {
	aggridHeaderClass,
	commonCodeCellStyle,
	commonNumberCellStyle,
	commonTextCellStyle,
} from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { RaonColDef } from "src/types/grid";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	restoreGridScrollState,
	saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import {
	createCellCssClassRules,
	GridCellConfig,
} from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	defaultKSK1120_R01DetailDto,
	KSK1120_R01DetailDto,
	KSK1120_R01InspectionValueField,
	KSK1120_R01_VALUE_FIELDS,
} from "./Dto_KSK1120_R01";
import { useKSK1120_R01DetailCellChange } from "./hooks/useKSK1120_R01DetailCellChange";
import { useKSK1120_R01Store } from "./Store_KSK1120_R01";
import {
	normalizeKSK1120_R01InspectionValue,
	normalizeKSK1120_R01ValueType,
} from "./Util_KSK1120_R01";

const COLUMN_STATE_KEY = "KSK1120_R01-detail-grid-column-state";
const EDITABLE_COLUMNS = [...KSK1120_R01_VALUE_FIELDS, "remark"];
const HEADER_NAMES: Record<string, string> = {
	rowIndex: "",
	inspItemNm: "검사항목",
	specText: "기준값",
	valueTypeNm: "결과유형",
	inspectValue1_1: "검사값1",
	inspectValue1_2: "검사값2",
	inspectValue1_3: "검사값3",
	inspectValue1_4: "검사값4",
	inspectValue1_5: "검사값5",
	remark: "비고",
};

interface DetailTableKSK1120R01Props {
	detailGridRef: React.RefObject<AgGridReact<KSK1120_R01DetailDto> | null>;
}

interface PassFailCellRendererParams extends ICellRendererParams<KSK1120_R01DetailDto, string> {
	valueField: KSK1120_R01InspectionValueField;
	onPassFailChange: (
		valueIndex: number,
		valueField: KSK1120_R01InspectionValueField,
		value: "Y" | "N" | "",
	) => void;
}

const PassFailCellRenderer = (params: PassFailCellRendererParams) => {
	if (params.node.rowPinned) return "";
	if (!params.data || normalizeKSK1120_R01ValueType(params.data.valueType) !== "PASS_FAIL") {
		return String(params.value ?? "");
	}

	const currentValue = params.data[params.valueField];
	const handleClick = (event: MouseEvent<HTMLButtonElement>, value: "Y" | "N") => {
		event.stopPropagation();
		params.onPassFailChange(
			params.data!.valueIndex,
			params.valueField,
			currentValue === value ? "" : value,
		);
	};

	return (
		<div className="h-full w-full flex items-center justify-center gap-[3px] px-[2px]">
			<button
				type="button"
				className={`h-[34px] min-w-[34px] flex-1 rounded-[4px] border font-bold ${
					currentValue === "Y"
						? "bg-blue-dark text-white border-blue-dark"
						: "bg-white text-blue-dark border-blue-dark"
				}`}
				onClick={(event) => handleClick(event, "Y")}
			>
				Y
			</button>
			<button
				type="button"
				className={`h-[34px] min-w-[34px] flex-1 rounded-[4px] border font-bold ${
					currentValue === "N"
						? "bg-red-600 text-white border-red-600"
						: "bg-white text-red-600 border-red-600"
				}`}
				onClick={(event) => handleClick(event, "N")}
			>
				N
			</button>
		</div>
	);
};

const getSpecText = (params: ValueGetterParams<KSK1120_R01DetailDto>): string => {
	if (!params.data) return "";
	if (!params.data.specValue1 && !params.data.specValue2) return "";
	return [
		params.data.specValue1,
		params.data.specRangeNm || params.data.specRange,
		params.data.specValue2,
		params.data.inspUnit,
	].filter(Boolean).join(" ");
};

export const DetailTable_KSK1120_R01 = ({
	detailGridRef,
}: DetailTableKSK1120R01Props) => {
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const {
		KSK1120_R01DetailList,
		updateKSK1120_R01DetailList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useKSK1120_R01Store();
	const { inspectionValueList } = useCommonCodeStore();
	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
	} = useGridSettingContent<KSK1120_R01DetailDto>({
		gridRef: detailGridRef,
		key: "valueIndex",
		gridData: KSK1120_R01DetailList,
		defaultGridData: defaultKSK1120_R01DetailDto,
		selectedRow: undefined,
	});
	const valueTypeNameMap = useMemo(() => new Map(
		inspectionValueList.map((code) => [code.minorCd, code.codeNm]),
	), [inspectionValueList]);
	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ["inspItemNm", "specText", "valueTypeNm"],
		insertedDisabledCells: [""],
		commonDisabledCells: [""],
		pointerCells: [...KSK1120_R01_VALUE_FIELDS, "remark"],
		insertedPointerCells: [""],
		commonPointerCells: [""],
		alignLeftCells: [""],
	}), []);
	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	const cellCssDefaultColDef = useMemo(() => ({
		...defaultColDef,
		cellClassRules: createCellClassRules<KSK1120_R01DetailDto>({
			idKey: "valueIndex",
			selectedList: [],
			deletedList: [],
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		}),
	}), [
		alignLeftColumns,
		createCellClassRules,
		disabledColumns,
		disabledInsertedColumns,
		pointerColumns,
		pointerInsertedColumns,
	]);
	const { handleCellValueChanged, handlePassFailChange } = useKSK1120_R01DetailCellChange({
		detailList: KSK1120_R01DetailList,
		updateKSK1120_R01DetailList,
	});
	const { handleCellKeyDown } = useDetailTableEvents<KSK1120_R01DetailDto>({
		detailList: KSK1120_R01DetailList,
		editableColumns: EDITABLE_COLUMNS,
		keyDownConfig: { enableArrowNavigation: true },
	});
	const createValueColumn = useCallback((
		field: KSK1120_R01InspectionValueField,
		headerName: string,
	): RaonColDef<KSK1120_R01DetailDto> => ({
		headerName,
		colId: field,
		field,
		width: 110,
		editable: (params) => {
			const valueType = normalizeKSK1120_R01ValueType(params.data?.valueType ?? "");
			return !params.node.rowPinned && (valueType === "NUMERIC" || valueType === "TEXT");
		},
		pasteable: false,
		cellRenderer: PassFailCellRenderer,
		cellRendererParams: {
			valueField: field,
			onPassFailChange: handlePassFailChange,
		},
		cellStyle: (params) => {
			const valueType = normalizeKSK1120_R01ValueType(params.data?.valueType ?? "");
			const alignmentStyle = valueType === "NUMERIC"
				? commonNumberCellStyle
				: valueType === "PASS_FAIL"
					? commonCodeCellStyle
					: commonTextCellStyle;
			if (params.node.rowPinned) return alignmentStyle;
			return {
				...alignmentStyle,
				backgroundColor: valueType === "PASS_FAIL" ? "#F8FAFC" : "#FFFFFF",
			};
		},
		valueParser: (params) => normalizeKSK1120_R01InspectionValue(
			params.data.valueType,
			params.newValue,
		),
	}), [handlePassFailChange]);

	const baseColumnDefs = useMemo<RaonColDef<KSK1120_R01DetailDto>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{ headerName: "검사항목", colId: "inspItemNm", field: "inspItemNm", width: 160, editable: false },
		{
			headerName: "기준값",
			colId: "specText",
			width: 200,
			editable: false,
			valueGetter: getSpecText,
		},
		{
			headerName: "결과유형",
			colId: "valueTypeNm",
			field: "valueTypeNm",
			width: 90,
			editable: false,
			cellStyle: (params) => params.node.rowPinned ? commonCodeCellStyle : undefined,
			valueFormatter: (params) => {
				const valueType = normalizeKSK1120_R01ValueType(params.data?.valueType ?? "");
				return valueTypeNameMap.get(valueType) || params.value || valueType;
			},
		},
		createValueColumn("inspectValue1_1", "검사값1"),
		createValueColumn("inspectValue1_2", "검사값2"),
		createValueColumn("inspectValue1_3", "검사값3"),
		createValueColumn("inspectValue1_4", "검사값4"),
		createValueColumn("inspectValue1_5", "검사값5"),
		{
			headerName: "비고",
			colId: "remark",
			field: "remark",
			width: 180,
			editable: (params) => !params.node.rowPinned,
			pasteable: false,
		},
	], [createValueColumn, valueTypeNameMap]);

	const columnDefs = useMemo<RaonColDef<KSK1120_R01DetailDto>[]>(() =>
		GridCustomColumnDefs<KSK1120_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState?.some((state) => state.colId === "rowIndex")
				? detailColumnState
				: null,
			headerNames: HEADER_NAMES,
			fallbackHeaderClass: aggridHeaderClass,
		}) as RaonColDef<KSK1120_R01DetailDto>[], [baseColumnDefs, detailColumnState]);

	const pinnedBottomRowData = useMemo<KSK1120_R01DetailDto[]>(() => [{
		...defaultKSK1120_R01DetailDto,
		inspItemNm: "합계",
		valueTypeNm: `${KSK1120_R01DetailList.length.toLocaleString()}건`,
	}], [KSK1120_R01DetailList.length]);

	const rowClassRules = useMemo(() =>
		createRowClassRules<KSK1120_R01DetailDto>({
			idKey: "valueIndex",
			selectedList: [],
			deletedList: [],
		}), [createRowClassRules]);

	const handleCellClick = useCallback((event: CellClickedEvent<KSK1120_R01DetailDto>) => {
		if (!event.data || event.node.rowPinned) return;
		const field = event.colDef.field;
		if (field === "remark" || (
			KSK1120_R01_VALUE_FIELDS.includes(field as KSK1120_R01InspectionValueField)
			&& normalizeKSK1120_R01ValueType(event.data.valueType) !== "PASS_FAIL"
		)) {
			event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: field! });
		}
	}, []);

	const handleBodyScrollEnd = useCallback((event: BodyScrollEndEvent<KSK1120_R01DetailDto>) => {
		if (event.direction === "vertical") saveGridScrollState(event.api, setDetailScrollTop);
	}, [setDetailScrollTop]);

	const handleFirstDataRendered = useCallback((event: FirstDataRenderedEvent<KSK1120_R01DetailDto>) => {
		restoreGridScrollState(event.api, detailScrollTop);
	}, [detailScrollTop]);

	const initDetailSelection = useCallback(() => {
		detailGridRef.current?.api.deselectAll();
	}, [detailGridRef]);

	return (
		<GridContextWrapper<KSK1120_R01DetailDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={detailGridRef}
			paddedGridData={KSK1120_R01DetailList}
			initSelected={initDetailSelection}
			defaultRowData={defaultKSK1120_R01DetailDto}
			baseColumnDefs={columnDefs}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			enableDataHistory={false}
		>
			<Grid_Excel<KSK1120_R01DetailDto>
				ref={detailGridRef}
				rowData={KSK1120_R01DetailList}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onCellClicked={handleCellClick}
				onCellKeyDown={handleCellKeyDown}
				onCellValueChanged={handleCellValueChanged}
				onBodyScrollEnd={handleBodyScrollEnd}
				onFirstDataRendered={handleFirstDataRendered}
				pinnedBottomRowData={pinnedBottomRowData}
				rowHeight={46}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
			/>
		</GridContextWrapper>
	);
};
